import {
    AuthenticationDetails,
    CognitoAccessToken,
    CognitoIdToken,
    CognitoRefreshToken,
    CognitoUser,
    CognitoUserSession,
} from "amazon-cognito-identity-js";
import { AuthChallenge, LoginResult } from "../../types/Auth";

export interface Session {
    identityToken: CognitoIdToken;
    accessToken: CognitoAccessToken;
    refreshToken: CognitoRefreshToken;
}

export interface ICognitoDriver {
    login(user: CognitoUser, password: string): Promise<LoginResult>;
    logout(user: CognitoUser): void;
    refreshSession(user: CognitoUser, token: CognitoRefreshToken): Promise<CognitoUserSession>;
    changePassword(user: CognitoUser, oldPassword: string, newPassword: string): Promise<void>;
    completeNewPassword(
        user: CognitoUser,
        newPassword: string,
        session?: CognitoUserSession,
    ): Promise<CognitoUserSession>;
    forgotPassword(user: CognitoUser): Promise<void>;
    confirmForgotPassword(user: CognitoUser, code: string, newPassword: string): Promise<void>;
}

export default class CognitoDriver implements ICognitoDriver {
    async login(user: CognitoUser, password: string): Promise<LoginResult> {
        const authDetails = new AuthenticationDetails({
            Username: user.getUsername(),
            Password: password,
        });

        return new Promise((resolve, reject) => {
            user.authenticateUser(authDetails, {
                onSuccess: function (session: CognitoUserSession) {
                    resolve({
                        success: true,
                        session: session,
                    });
                },
                onFailure: (error) => {
                    reject(error);
                },
                newPasswordRequired: function (userAttributes: unknown, requiredAttributes: string[]) {
                    resolve({
                        success: false,
                        challengeData: {
                            challenge: AuthChallenge.NEW_PASSWORD_REQUIRED,
                            user: user,
                            requiredAttributes: requiredAttributes,
                        },
                    });
                },
            });
        });
    }

    logout(user: CognitoUser) {
        // globalSignOut signs out the user from all devices and clears all cached sessions
        user.globalSignOut({
            onSuccess: () => {
                user.signOut();
            },
            onFailure: () => {
                user.signOut();
            },
        });
    }

    async refreshSession(user: CognitoUser, token: CognitoRefreshToken): Promise<CognitoUserSession> {
        return new Promise((resolve, reject) => {
            user.refreshSession(token, (error, session: CognitoUserSession) => {
                if (error) {
                    reject(error);
                }
                resolve(session);
            });
        });
    }

    async changePassword(user: CognitoUser, oldPassword: string, newPassword: string): Promise<void> {
        return new Promise((resolve, reject) => {
            user.changePassword(oldPassword, newPassword, function (err) {
                if (err) {
                    reject(err);
                }
                resolve(undefined);
            });
        });
    }

    async completeNewPassword(user: CognitoUser, newPassword: string): Promise<CognitoUserSession> {
        return new Promise((resolve, reject) => {
            user.completeNewPasswordChallenge(
                newPassword,
                {},
                {
                    onSuccess: function (session: CognitoUserSession) {
                        resolve(session);
                    },
                    onFailure: function (err: unknown) {
                        reject(err);
                    },
                },
            );
        });
    }

    async forgotPassword(user: CognitoUser): Promise<void> {
        return new Promise((resolve, reject) => {
            user.forgotPassword({
                onSuccess: () => {
                    resolve();
                },
                onFailure: (err) => {
                    alert(err.message || JSON.stringify(err));
                    reject(err);
                },
            });
        });
    }

    async confirmForgotPassword(user: CognitoUser, code: string, newPassword: string): Promise<void> {
        return new Promise((resolve, reject) => {
            user.confirmPassword(code, newPassword, {
                onSuccess: () => {
                    resolve();
                },
                onFailure: (err) => {
                    reject(err);
                },
            });
        });
    }
}
